import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TicketReview } from '../db/TicketReview.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { getCurrentAppUser, isCustomerOfTicket, snAssignedTo } from './_assignee-guard.js';

/**
 * Customer submits a review for a ticket. One review per (ticket, customer) — re-submitting
 * overwrites the previous one. Server verifies that:
 *   - caller holds `TicketReview` (Customer-only by role config)
 *   - caller is in the Customer group
 *   - the ticket's client sys_id matches one of the caller's linked customers (defence in depth)
 */
export const submitTicketReview = registerMutation({
  operationName: 'submitTicketReview',
  type: () => TicketReview,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'rating', type: () => Int },
    { name: 'successfullyCompleted', type: () => Boolean },
    { name: 'comment', type: () => String },
  ],
  resolve:
    (ticketNumber: string, rating: number, successfullyCompleted: boolean, comment?: string) =>
    async (_gql: BaseQLSelectionSet<TicketReview>) => {
      const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketReview));
      if (!allowed) throw new Error('Insufficient permissions');

      const appUser = await getCurrentAppUser();
      if (appUser.userGroup !== SmartTeamsAppUserGroup.Customer) {
        throw new Error('Only customers can submit ticket reviews');
      }
      if (rating < 1 || rating > 5) throw new Error('Rating must be between 1 and 5');

      const { getTicketDetails } = await import('@nsc/servicenow/smartteam-api.js');
      const sn = await getTicketDetails(ticketNumber);
      const ticketClientSysId = sn.customer?.reference?.value;

      const owns = await isCustomerOfTicket({
        appUser,
        ticketNumber,
        ticketClientSysId,
      });
      if (!owns) {
        throw new Error('You can only review tickets for your own organisation');
      }

      // Enforce the UI's terminal-state rule server-side: reviews only for closed/cancelled (3/4/7).
      const ticketState = sn.state?.value;
      if (ticketState !== '3' && ticketState !== '4' && ticketState !== '7') {
        throw new Error('Reviews can only be submitted for completed or cancelled tickets');
      }

      const feSysId = snAssignedTo(sn)?.value;

      const col = await getRawCollection<TicketReview>(TicketReview._collectionName);
      const now = new Date();
      await col.updateOne(
        { ticketNumber, customerUserId: appUser.userId },
        {
          $set: {
            ticketNumber,
            customerUserId: appUser.userId,
            feSysId: feSysId || null,
            rating,
            successfullyCompleted,
            comment: comment || null,
          },
          $setOnInsert: { createdAt: now },
        },
        { upsert: true },
      );

      const saved = await col.findOne({ ticketNumber, customerUserId: appUser.userId });
      return new TicketReview(saved!);
    },
});

/**
 * Fetch the current customer's review for a ticket (or null if unsubmitted).
 * Used by the ticket detail page to gate the review form.
 */
export const getMyTicketReview = registerQuery({
  operationName: 'getMyTicketReview',
  type: () => TicketReview,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gql: BaseQLSelectionSet<TicketReview>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketView));
    if (!allowed) throw new Error('Insufficient permissions');

    const appUser = await getCurrentAppUser();
    const col = await getRawCollection<TicketReview>(TicketReview._collectionName);
    const review = await col.findOne({ ticketNumber, customerUserId: appUser.userId });
    return review ? new TicketReview(review) : null;
  },
});
